import { Data } from '@dnd-kit/core';
import { Droppable } from '@payaca/dnd';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import React, { FC } from 'react';
import ConditionalWrapper from '../ConditionalWrapper/ConditionalWrapper';

export type BreadcrumbItem = {
  id?: string;
  label: string;
  onClick?: () => void;
  to?: string;
  href?: string;
  data?: Data;
};

export interface IProps {
  className?: string;
  itemsAreDroppable?: boolean;
  items: BreadcrumbItem[];
  LinkComponent?: React.ElementType;
}

export const BREADCRUMB_DROP_TYPE = 'breadcrumb-item';

const Breadcrumb: FC<IProps> = (props) => {
  const { className, items, itemsAreDroppable = false, LinkComponent } = props;

  if (items.length === 0) return null;

  return (
    <ol
      style={{ '--breadcrumb-items': items.length } as React.CSSProperties}
      data-breadcrumb-items={items.length}
      className={clstx(
        'group typography:prose m-0 grid whitespace-nowrap p-0 @container',
        // for the number of items, create that many columns that attempt to be
        // as wide as their content, but not wider than the container
        'grid-cols-[repeat(var(--breadcrumb-items),minmax(auto,max-content))]',
        className
      )}
      aria-label="Breadcrumb"
    >
      {items.map((i, index) => {
        const isLast = index === items.length - 1;
        let Element: any = 'a';
        if (isLast) {
          Element = 'span';
        } else if (i.to && LinkComponent) {
          Element = LinkComponent;
        } else if (i.to) {
          Element = 'a';
        } else if (i.onClick) {
          Element = 'button';
        }

        return (
          <ConditionalWrapper
            key={i.id || index}
            condition={itemsAreDroppable}
            Wrapper={Droppable}
            wrapperProps={{
              id: `breadcrumb::${i.id || index}`,
              data: {
                type: BREADCRUMB_DROP_TYPE,
                breadcrumbItemId: i.id || index,
                ...i.data,
              },
              disabled: !itemsAreDroppable || isLast,
            }}
          >
            <li
              className={clstx(
                'group/item flex items-center overflow-hidden',
                // make every item hidden apart from the second the last one
                '[&:nth-last-child(2)]:flex-row-reverse [&:nth-last-child(2)_svg]:rotate-180 [&:not(:nth-last-child(2))]:hidden',
                // if there is only one item however, override that fact that it's hidden
                'group-data-[breadcrumb-items=1]:flex',
                // on medium container size, show all items
                '@md:[&:nth-last-child(2)]:flex-row @md:[&:nth-last-child(2)_svg]:rotate-0 @md:[&:not(:nth-last-child(2))]:flex'
              )}
            >
              <Element
                className={clstx(
                  isLast && 'truncate font-medium dark:text-gray-200',
                  !isLast && [
                    'group-data-[over]/item:ring-2 group-data-[over]/item:ring-blue-500',
                    'truncate bg-transparent p-0 cursor-pointer text-gray-500',
                    'hover:text-blue-600 hover:no-underline',
                    'focus:text-blue-600 focus:outline-none dark:focus:text-blue-500',
                  ]
                )}
                to={i.to}
                href={i.to || i.href}
                onClick={i.onClick}
                aria-current={isLast && 'page'}
                title={i.label}
              >
                {i.label}
              </Element>
              {!isLast && (
                <UntitledIcon
                  name="chevron-right"
                  className="mx-2 h-4 w-4 shrink-0 overflow-visible text-gray-400 dark:text-gray-600"
                />
              )}
            </li>
          </ConditionalWrapper>
        );
      })}
    </ol>
  );
};

export default Breadcrumb;
